import { supabase } from '@/integrations/supabase/client';
import { CommunityAIService, AssistantConfig } from './community-ai.service';
import { AIMentionService } from './ai-mention.service';
import { extractMentions, hasMentions } from '@/utils/mention-utils';
import { logInfo, logError, LogCategory } from '@/utils/logger';

export interface MentionTriggeredResponse {
  success: boolean;
  mentionedAgents: string[];
  responses: Array<{
    assistantId: string;
    assistantName: string;
    success: boolean;
    error?: string;
  }>;
}

/**
 * Service for handling mention-triggered AI responses
 */
export class MentionTriggeredAIService {
  /**
   * Process mentions in reply content and trigger AI responses
   * @param replyContent - The content of the reply to scan for mentions
   * @param discussionId - The ID of the discussion
   * @param originalUserId - The user ID to use for AI responses (for RLS compliance)
   * @returns Result of mention processing and AI responses
   */
  static async processMentionsInReply(
    replyContent: string,
    discussionId: string,
    originalUserId: string
  ): Promise<MentionTriggeredResponse> {
    logInfo('Processing mentions in reply', {
      category: LogCategory.API,
      data: {
        discussionId,
        contentLength: replyContent.length,
        hasMentions: hasMentions(replyContent)
      }
    });

    // Extract mentions from content
    const mentionNames = extractMentions(replyContent);
    
    if (mentionNames.length === 0) {
      logInfo('No mentions found in reply content', {
        category: LogCategory.API,
        data: { discussionId }
      });
      
      return {
        success: true,
        mentionedAgents: [],
        responses: []
      };
    }

    logInfo('Found mentions in reply', {
      category: LogCategory.API,
      data: {
        discussionId,
        mentionNames,
        mentionCount: mentionNames.length
      }
    });

    try {
      // Get the discussion to find the OpenAI thread ID
      const { data: discussion, error: discussionError } = await supabase
        .from('community_discussions')
        .select('openai_thread_id')
        .eq('id', discussionId)
        .single();

      if (discussionError) {
        throw new Error(`Failed to get discussion: ${discussionError.message}`);
      }

      if (!discussion.openai_thread_id) {
        logError('Discussion has no OpenAI thread ID', {
          category: LogCategory.API,
          data: { discussionId }
        });
        
        return {
          success: false,
          mentionedAgents: mentionNames,
          responses: mentionNames.map(name => ({
            assistantId: '',
            assistantName: name,
            success: false,
            error: 'Discussion has no OpenAI thread'
          }))
        };
      }

      // Find AI assistants for the mentioned names
      const mentionedAssistants = await AIMentionService.getAssistantsByNames(mentionNames);
      
      if (mentionedAssistants.length === 0) {
        logInfo('No valid AI assistants found for mentions', {
          category: LogCategory.API,
          data: { discussionId, mentionNames }
        });
        
        return {
          success: true,
          mentionedAgents: mentionNames,
          responses: mentionNames.map(name => ({
            assistantId: '',
            assistantName: name,
            success: false,
            error: 'AI assistant not found'
          }))
        };
      }

      logInfo('Triggering AI responses for mentioned assistants', {
        category: LogCategory.API,
        data: {
          discussionId,
          mentionedAssistants: mentionedAssistants.map(a => a.name),
          openaiThreadId: discussion.openai_thread_id.substring(0, 10) + '...'
        }
      });

      // Trigger AI responses for each mentioned assistant
      const responsePromises = mentionedAssistants.map(async (assistant) => {
        try {
          logInfo('Generating AI response for mentioned assistant', {
            category: LogCategory.API,
            data: {
              discussionId,
              assistantName: assistant.name,
              assistantId: assistant.id
            }
          });

          // Use the existing CommunityAIService to trigger the response
          const aiResponse = await CommunityAIService.triggerAIResponse(
            replyContent, // Use the reply content as context - will be enhanced with forum context
            discussionId,
            assistant.openai_assistant_id,
            discussion.openai_thread_id
          );

          // Save the AI response as a community reply
          await CommunityAIService.saveAIResponseAsReply(
            aiResponse,
            discussionId,
            assistant,
            originalUserId
          );

          logInfo('Successfully generated AI response for mention', {
            category: LogCategory.API,
            data: {
              discussionId,
              assistantName: assistant.name,
              responseLength: aiResponse.response.length
            }
          });

          return {
            assistantId: assistant.id,
            assistantName: assistant.name,
            success: true
          };
        } catch (error: any) {
          logError('Failed to generate AI response for mention', {
            category: LogCategory.API,
            data: {
              error: error.message,
              discussionId,
              assistantName: assistant.name,
              assistantId: assistant.id
            }
          });

          return {
            assistantId: assistant.id,
            assistantName: assistant.name,
            success: false,
            error: error.message
          };
        }
      });

      // Wait for all responses to complete
      const responses = await Promise.all(responsePromises);
      
      const successCount = responses.filter(r => r.success).length;
      const errorCount = responses.filter(r => !r.success).length;

      logInfo('Mention-triggered AI responses completed', {
        category: LogCategory.API,
        data: {
          discussionId,
          totalMentions: mentionNames.length,
          foundAssistants: mentionedAssistants.length,
          successCount,
          errorCount
        }
      });

      return {
        success: errorCount === 0,
        mentionedAgents: mentionNames,
        responses
      };
    } catch (error: any) {
      logError('Failed to process mentions in reply', {
        category: LogCategory.API,
        data: {
          error: error.message,
          discussionId,
          mentionNames
        }
      });

      return {
        success: false,
        mentionedAgents: mentionNames,
        responses: mentionNames.map(name => ({
          assistantId: '',
          assistantName: name,
          success: false,
          error: error.message
        }))
      };
    }
  }

  /**
   * Check if content has valid AI mentions
   * @param content - The content to check
   * @returns True if content has mentions that could trigger AI responses
   */
  static async hasValidAIMentions(content: string): Promise<boolean> {
    if (!hasMentions(content)) {
      return false;
    }

    const mentionNames = extractMentions(content);
    const mentionedAssistants = await AIMentionService.getAssistantsByNames(mentionNames);
    
    return mentionedAssistants.length > 0;
  }
} 